import { ReactNode } from 'react';

import { DEFAULT_VIDEO_PROPS } from 'lib/constants';
import { optimizeAsset } from 'utils/imgix';

import { CollectionPreview } from 'types/Collection';
import { MarketNft } from 'types/Nft';
import { PreviewMediaAsset } from 'types/media';

import Media from './Media';
import Box from './base/Box';
import Skeleton from './base/Skeleton';
import Text from './base/Text';

const LOGO_SIZE = 72;
const COLLECTION_LOGO_SIZE = 18;

interface NftTagScaffoldingProps {
  name: ReactNode;
  logo: ReactNode;
  collection: ReactNode;
}
function NftTagScaffolding(props: NftTagScaffoldingProps) {
  const { name, logo, collection } = props;

  return (
    <Box css={{ display: 'flex', gap: '$4', alignItems: 'center' }}>
      {logo}
      <Box
        css={{
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          overflow: 'hidden',
        }}
      >
        {collection}
        {name}
      </Box>
    </Box>
  );
}

function NftTagSkeleton() {
  return (
    <NftTagScaffolding
      logo={
        <Skeleton.Block
          css={{ borderRadius: '$3', width: LOGO_SIZE, height: LOGO_SIZE }}
        />
      }
      collection={<Skeleton.Block css={{ width: 110, height: 18 }} />}
      name={<Skeleton.Block css={{ width: 90, height: 20 }} />}
    />
  );
}

function optimizeNftTagMedia(media: PreviewMediaAsset): PreviewMediaAsset {
  if (media.type === 'video') {
    return {
      ...media,
      src: optimizeAsset(media.src, {
        w: 140,
        fm: 'mp4',
      }),
      poster: optimizeAsset(media.poster, {
        w: 140,
        fm: 'jpg',
      }),
    };
  }
  return {
    ...media,
    src: optimizeAsset(media.src, {
      w: 140,
    }),
  };
}

function NftTagBase(props: {
  collection: CollectionPreview;
  name: MarketNft['name'];
  media: PreviewMediaAsset | null;
}) {
  const { collection, name, media } = props;

  const getMedia = () => {
    const optimizedMedia = media ? optimizeNftTagMedia(media) : null;

    if (!optimizedMedia) {
      return null;
    }
    if (optimizedMedia.type === 'video') {
      return (
        <NftTagMediaWrapper>
          <Media
            as="video"
            src={optimizedMedia.src}
            poster={optimizedMedia.poster}
            {...DEFAULT_VIDEO_PROPS}
          />
        </NftTagMediaWrapper>
      );
    }
    return (
      <NftTagMediaWrapper>
        <Media src={optimizedMedia.src} />
      </NftTagMediaWrapper>
    );
  };

  const collectionLogo = collection.collectionImageUrl
    ? optimizeAsset(collection.collectionImageUrl, { w: 24 })
    : null;

  return (
    <NftTagScaffolding
      name={
        <Text size={3} weight="semibold" ellipsis>
          {name}
        </Text>
      }
      collection={
        <Box
          css={{
            display: 'flex',
            flexDirection: 'row',
            gap: '$1',
            alignItems: 'center',
          }}
        >
          {collectionLogo && (
            <CollectionLogoWrapper>
              <Media src={collectionLogo} />
            </CollectionLogoWrapper>
          )}
          <Text size={1} color="dim" weight="semibold" ellipsis>
            {collection.name}
          </Text>
        </Box>
      }
      logo={getMedia()}
    />
  );
}

function NftTagMediaWrapper(props: { children: ReactNode }) {
  return (
    <Media.Root
      css={{
        height: LOGO_SIZE,
        width: LOGO_SIZE,
        flexShrink: 0,
        borderRadius: '$3',

        [`& ${Media}`]: {
          backgroundColor: '$black5',
        },
      }}
    >
      <Media.Tint css={{ height: 'inherit', width: 'inherit' }}>
        {props.children}
      </Media.Tint>
    </Media.Root>
  );
}

function CollectionLogoWrapper(props: { children: ReactNode }) {
  return (
    <Media.Root
      css={{
        width: COLLECTION_LOGO_SIZE,
        height: COLLECTION_LOGO_SIZE,
        flexShrink: 0,
        borderRadius: '$1',
        [`& ${Media}`]: {
          backgroundColor: '$black5',
        },
      }}
    >
      <Media.Tint css={{ height: 'inherit', width: 'inherit' }}>
        {props.children}
      </Media.Tint>
    </Media.Root>
  );
}

const NftTag = Object.assign(NftTagBase, {
  Skeleton: NftTagSkeleton,
});

export default NftTag;
